(ns frontend.view.settings2.permissions.pipeline
  (:require [common.localization :as loc]
            [common.permissions.pipeline-permissions :as pipeline-permissions]
            [frontend.actions.tenant :as tenant-actions]
            [frontend.logging :as logging]
            [frontend.page-components.default-page-layout :as default-page-layout]
            [frontend.page-components.zap-toolbar :as zap-toolbar]
            [frontend.routing :as routing]

            [frontend.state.current-session :as session]
            [frontend.view.entity-viewers.utils :as entity-utils]
            [frontend.view.not-found :as not-found]
            [frontend.view.settings2.permissions.field-settings.db :as db]
            [frontend.view.settings2.permissions.field-settings.table :as settings-table]
            [frontend.view.syncing-data :as syncing]
            [potpuri.core :as potpuri]
            [reagent.core :as r]
            [reagent.impl.component :as reagent-component]
            [ui.components.breadcrumbs.view :as breadcrumbs]
            [ui.components.button.view :as button]
            [ui.components.tab-strip.view :as tab-strip]
            [ui.dom-globals :as dg]))

(defonce force-remount-atom (r/atom 0))
(defn force-remount! []
  (swap! force-remount-atom inc))

(defn toolbar-buttons [{:keys [pipeline-id zadmin-state settings-state table-state]} _toolbar-state]
  [{:toolbar-button?  true
    :dropdown-action? true
    :ord              1
    :primary-button?  false
    :disabled?        (and (empty? @zadmin-state)
                           (empty? @settings-state))
    :icon             :fa-star
    :on-select        (fn [_]
                        (let [table-state-snapshot @table-state
                              zadmin-state-snapshot @zadmin-state
                              settings-state-snapshot @settings-state
                              ts-before-request (:common/updated-on pipeline-id)]
                          (tenant-actions/*->set-field-settings
                           {:pipeline-id       pipeline-id
                            :pipeline-settings (potpuri/deep-merge
                                                (->> (get @session/entities pipeline-id) :pipeline/settings)
                                                @settings-state)}
                           (fn [_success]
                             (session/wait-for-entity-update! pipeline-id ts-before-request
                                                              (fn [_]
                                                                ;; after processing done clear local settings --> ONLY if nothing has changed
                                                                (when (and (= @table-state table-state-snapshot)
                                                                           (= @zadmin-state zadmin-state-snapshot)
                                                                           (= @settings-state settings-state-snapshot))

                                                                  (reset! zadmin-state {})
                                                                  (reset! settings-state {}))
                                                                ;; if atom is cursor we must flush it
                                                                (deref zadmin-state)
                                                                (deref settings-state))))
                           (fn [err]
                             (logging/error "Failed to save settings" err)))))
    :label            (loc/tr :save)}])

(def pipeline-type-info
  ;;pipeline-type => [routing-key localization-key]
  {:entity.type/oppo
   [:settings/deal-flow-pipelines :settings/deal-flow-pipelines]
   :entity.type/fundraising-opportunity
   [:settings/fundraising-pipelines :settings/fundraising-pipelines]
   :entity.type/sales-opportunity
   [:settings/sales-pipelines :settings/sales-pipelines]
   :entity.type/exit-opportunity
   [:settings/exit-pipelines :settings/exit-pipelines]})

(defn breadcrumbs [{pipeline-name :pipeline/name
                    pipeline-type :pipeline/entity-type}]
  (let [[route title] (get pipeline-type-info pipeline-type)]
    [breadcrumbs/container
     [breadcrumbs/item
      {:on-click (fn [_e]
                   (routing/set-view! [:account-settings :tab-key "config"]))}
      (loc/tr :settings)]
     [breadcrumbs/item
      {:on-click (fn [_e]
                   (routing/set-view! [route]))}
      (loc/tr title)]
     [breadcrumbs/item
      {:active? true}
      (loc/tr :pipeline-permissions-title [pipeline-name])]]))

(defn component [{:keys [pipeline-id]}]
  (let [toolbar-atom (r/atom {})
        entity-loading-state (r/atom {})
        _ (entity-utils/reset-state! entity-loading-state pipeline-id)
        pipeline (get @session/entities pipeline-id)
        table-state (r/atom (if-not pipeline
                              {}
                              {:entity-type (:pipeline/entity-type pipeline)}))
        _ (swap! db/view-settings assoc :sort-by :field-settings/field-name :sort-direction "ASC")
        zadmin-state (r/atom {})
        settings-state (r/atom {})
        args {:pipeline-id    (:db/id pipeline)
              :zadmin-state   zadmin-state
              :settings-state settings-state
              :table-state    table-state}]
    (r/create-class
     {:component-did-update
      (fn [this old-argv]
        (let [prev-id (-> old-argv
                          reagent-component/extract-props
                          :pipeline-id)
              new-id (-> this
                         r/props
                         :pipeline-id)]
          (when-not (= prev-id new-id)
            (force-remount!))))
      :reagent-render
      (fn [{:keys [pipeline-id]}]
        (let [pipeline (session/get-entity pipeline-id)]
          (cond (or (:common/disabled? pipeline)
                    (:not-found @entity-loading-state))
                [not-found/not-found-content]

                (:loading @entity-loading-state)
                [syncing/view]

                :else
                (if (or (not pipeline)
                        (:common/disabled? pipeline))
                  [:div " "]
                  (let [pipeline (get @session/entities pipeline-id)
                        pipeline-entity (:pipeline/entity-type pipeline)
                        config-entity-types (pipeline-permissions/pipeline-configuration-entity-types
                                             pipeline)]
                    [:<>
                     [zap-toolbar/view toolbar-atom
                      (partial breadcrumbs pipeline)
                      nil
                      (partial toolbar-buttons args)]

                     [:div
                      {:class "py-4 px-4 flex flex-col flex-1 gap-4"}
                      [:div
                       {:class "px-3"}
                       [tab-strip/view
                        {:on-change!   (fn [tab-key]
                                         (swap! table-state assoc :entity-type tab-key :show-inactive? false :search nil)
                                         (swap! db/view-settings assoc :sort-by :field-settings/field-name :sort-direction "ASC")
                                         ;; We MUST deref atom in case it's cursor - there is no way to know so we should do it on every swap!
                                         (= (get @table-state :entity-type) tab-key))
                         :tabs         (concat [{:value pipeline-entity
                                                 :label (loc/tr pipeline-entity)}]
                                               (->> config-entity-types
                                                    (map (fn [at]
                                                           {:value at
                                                            :label (loc/tr at)}))
                                                    (into [])))
                         :selected-tab (:entity-type @table-state)
                         :default-tab  pipeline-entity}]]
                      [:div
                       {:class "flex items-center gap-4"}

                       [:div
                        {:class "min-w-72 relative"}
                        [:input
                         {:class       "input-area py-2 px-3 w-full text-input-light-text placeholder:text-input-light-text-paler outline-0 border border-solid border-input-light-border-paler hover:border-input-light-border focus:ring-1 focus:ring-inset focus:ring-input-light-border focus-visible:ring-input-light-border rounded-full bg-base-light"
                          :type        "text"
                          :on-click    identity
                          :value       (:search @table-state)
                          :on-change   (fn [e]
                                         (swap! table-state assoc :search (dg/event-target-value e)))
                          :placeholder (loc/tr :search-field)}]
                        [:i
                         {:class "fal fa-search absolute top-2/4 -translate-y-2/4 right-3"}]]
                       (let [{:keys [show-inactive?]} @table-state]
                         [button/secondary-open
                          {:class    "mx-3"
                           :tooltip  (loc/tr :activate-additional-fields-info)
                           :on-click (fn [e]
                                       (dg/prevent-and-stop e)
                                       (swap! table-state update :show-inactive? not))}
                          (if show-inactive?
                            (loc/tr :hide-inactive-fields)
                            (loc/tr :show-all-available-fields))
                          [:i
                           {:class "fa fal fa-info-circle"}]])]
                      [:div
                       {:class "flex-1"
                        :id    (str "permissions-" (:entity-type @table-state))}
                       [settings-table/view {:pipeline-id        (:db/id pipeline)
                                             :table-state        table-state
                                             :zadmin-state       zadmin-state
                                             :settings-state     settings-state
                                             :local-storage-path [pipeline-id :pipeline/settings]}]]]])))))})))

(defmethod routing/render-view :settings.permissions/pipeline [{:keys [route-params _view-params]}]
  (let [id (:id route-params)
        pipeline-id (when-let [parsed-id (js/parseInt id 10)]
                      (when-not (js/isNaN parsed-id)
                        parsed-id))]
    [default-page-layout/view
     ^{:key (str "pipeline-permissions-viewer-id-" @force-remount-atom)}
     [component {:pipeline-id pipeline-id}]]))
